import React, { useState, useEffect, useCallback } from 'react';
import { Search, Filter, TrendingUp, TrendingDown, Edit, Trash2, RefreshCw } from 'lucide-react';
import Sidebar from '../components/ui/Sidebar';
import { toast } from 'sonner';
import { getCamundaService } from '../../infrastructure/camunda';
import type { ProcessDefinitionStatistics, HistoricProcessInstance } from '../../infrastructure/camunda';

interface ProcessAnalytics {
  id: string;
  name: string;
  definitionKey: string;
  instances: number;
  completedInstances: number;
  runningInstances: number;
  incidents: number;
  automationRate: number;
  manualTasks: number;
  botHours: number;
  costSavings: number;
  roi: number;
  category: 'High ROI' | 'Good ROI' | 'Low ROI';
  selected?: boolean;
}

interface KPICard {
  title: string;
  value: string;
  change: number;
  unit: string;
}

const AnalyticsPage: React.FC = () => {
  const [selectedTab, setSelectedTab] = useState<'all' | 'active' | 'issues'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTimeFilter, setSelectedTimeFilter] = useState('12 months');
  const [processes, setProcesses] = useState<ProcessAnalytics[]>([]);
  const [loading, setLoading] = useState(true);
  const [kpiCards, setKpiCards] = useState<KPICard[]>([
    { title: 'Average automation rate', value: '0', change: 0, unit: '%' },
    { title: 'Total instances', value: '0', change: 0, unit: '' },
    { title: 'Active processes', value: '0', change: 0, unit: '' },
  ]);

  const camunda = getCamundaService();

  // Fetch process analytics from Camunda
  const fetchAnalytics = useCallback(async () => {
    try {
      setLoading(true);

      // Get process definition statistics
      const stats = await camunda.processDefinition.getStatistics({
        failedJobs: true,
        incidents: true,
      });

      // Get historic process instances for completed count
      const historicInstances = await camunda.history.getProcessInstances({
        finished: true,
        maxResults: 1000,
      });

      // Group historic instances by process definition
      const completedByProcess: Record<string, number> = {};
      historicInstances.forEach((instance: HistoricProcessInstance) => {
        const key = instance.processDefinitionKey || 'unknown';
        completedByProcess[key] = (completedByProcess[key] || 0) + 1;
      });

      // Build analytics data
      const analyticsData: ProcessAnalytics[] = stats.map((stat: ProcessDefinitionStatistics) => {
        const def = stat.definition || stat;
        const totalIncidents = stat.incidents?.reduce((sum, inc) => sum + inc.incidentCount, 0) || 0;
        const completedCount = completedByProcess[def.key] || 0;
        const totalInstances = stat.instances + completedCount;

        // Calculate automation rate (completed without incidents / total)
        const automationRate = totalInstances > 0
          ? Math.round(((completedCount - totalIncidents) / Math.max(totalInstances, 1)) * 100 * 10) / 10
          : 0;

        // Estimate manual tasks (incidents + failed jobs require manual intervention)
        const manualTasks = totalIncidents + stat.failedJobs;

        // Estimate bot hours (running instances * avg 0.5 hours)
        const botHours = Math.round(stat.instances * 0.5);

        // Estimate cost savings ($10 per automated instance)
        const costSavings = (completedCount - manualTasks) * 10;

        // Calculate ROI
        const roi = costSavings > 0 ? Math.round((costSavings / Math.max(manualTasks * 5, 1)) * 100) : 0;

        // Determine category
        let category: ProcessAnalytics['category'] = 'Low ROI';
        if (roi >= 200) category = 'High ROI';
        else if (roi >= 100) category = 'Good ROI';

        return {
          id: def.id,
          name: def.name || def.key,
          definitionKey: def.key,
          instances: totalInstances,
          completedInstances: completedCount,
          runningInstances: stat.instances,
          incidents: totalIncidents,
          automationRate: Math.max(0, automationRate),
          manualTasks,
          botHours,
          costSavings: Math.max(0, costSavings),
          roi: Math.max(0, roi),
          category,
        };
      });

      setProcesses(analyticsData);

      // Calculate KPIs
      const totalInstances = analyticsData.reduce((sum, p) => sum + p.instances, 0);
      const avgAutomation = analyticsData.length > 0
        ? Math.round(analyticsData.reduce((sum, p) => sum + p.automationRate, 0) / analyticsData.length * 10) / 10
        : 0;
      const activeProcesses = analyticsData.filter(p => p.runningInstances > 0).length;

      setKpiCards([
        { title: 'Average automation rate', value: avgAutomation.toString(), change: 8.5, unit: '%' },
        { title: 'Total instances', value: totalInstances.toLocaleString(), change: 15.2, unit: '' },
        { title: 'Active processes', value: activeProcesses.toString(), change: 5.0, unit: '' },
      ]);

    } catch (error) {
      console.error('Error fetching analytics:', error);
      toast.error('Failed to fetch analytics data');
    } finally {
      setLoading(false);
    }
  }, [camunda]);

  // Initial load
  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  const getCategoryColor = (category: ProcessAnalytics['category']) => {
    switch (category) {
      case 'High ROI':
        return 'bg-green-100 text-green-700';
      case 'Good ROI':
        return 'bg-blue-100 text-blue-700';
      case 'Low ROI':
        return 'bg-orange-100 text-orange-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const toggleProcessSelection = (id: string) => {
    setProcesses(processes.map(process =>
      process.id === id ? { ...process, selected: !process.selected } : process
    ));
  };

  const formatNumber = (num: number) => {
    return num.toLocaleString();
  };

  const formatCurrency = (num: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(num);
  };

  // Pagination
  const pageSize = 10;
  const [currentPage, setCurrentPage] = useState(0);
  const totalPages = Math.ceil(processes.length / pageSize);
  const paginatedProcesses = processes.slice(currentPage * pageSize, (currentPage + 1) * pageSize);

  return (
    <div className="min-h-screen bg-gray-50 flex">
      <Sidebar />

      <div className="flex-1 ml-16 mr-4 mt-4 mb-4">
        <div className="h-full bg-white rounded-2xl shadow-sm overflow-hidden flex flex-col">
          <header className="bg-white border-b border-gray-200 px-6 py-4 rounded-t-2xl flex-shrink-0">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <TrendingUp size={20} className="text-purple-600" />
                </div>
                <div>
                  <h1 className="text-xl font-semibold text-gray-900">Process Analytics</h1>
                  <p className="text-sm text-gray-500">Monitor and optimize your Camunda processes</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="relative">
                  <Search size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search processes..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 pr-4 py-2 w-64 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:bg-white"
                  />
                </div>
                <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
                  {['12m', '30d', '7d', '24h'].map((filter) => (
                    <button
                      key={filter}
                      onClick={() => setSelectedTimeFilter(filter)}
                      className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${selectedTimeFilter === filter
                          ? 'bg-white text-purple-600 shadow-sm'
                          : 'text-gray-600 hover:text-gray-900'
                        }`}
                    >
                      {filter}
                    </button>
                  ))}
                </div>
                <button
                  onClick={() => fetchAnalytics()}
                  className="p-2 text-gray-500 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                  disabled={loading}
                >
                  <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
                </button>
              </div>
            </div>
          </header>

          <main className="p-6 flex-1 overflow-auto">

            {/* KPI Cards */}
            <div className="grid grid-cols-3 gap-6 mb-8">
              {kpiCards.map((kpi, index) => (
                <div key={index} className="bg-white rounded-lg border border-gray-200 p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-sm font-medium text-gray-600">{kpi.title}</h3>
                    <div className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${kpi.change > 0 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                      }`}>
                      {kpi.change > 0 ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
                      {Math.abs(kpi.change)}%
                    </div>
                  </div>
                  <div className="flex items-baseline gap-2">
                    <span className="text-3xl font-bold text-gray-900">
                      {kpi.unit === '$' ? kpi.unit : ''}
                      {kpi.value}
                      {kpi.unit === '%' ? kpi.unit : ''}
                    </span>
                    {kpi.unit === '$' && (
                      <span className="text-sm text-gray-500">USD</span>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Process Details Section */}
            <div className="bg-white rounded-lg border border-gray-200">
              {/* Tabs */}
              <div className="flex items-center justify-between p-4 border-b border-gray-200">
                <div className="flex items-center gap-6">
                  {[
                    { key: 'all', label: 'View all' },
                    { key: 'active', label: 'Active' },
                    { key: 'issues', label: 'Issues' },
                  ].map((tab) => (
                    <button
                      key={tab.key}
                      onClick={() => setSelectedTab(tab.key as any)}
                      className={`pb-2 px-1 text-sm font-medium border-b-2 transition-colors ${selectedTab === tab.key
                          ? 'text-blue-600 border-blue-600'
                          : 'text-gray-600 border-transparent hover:text-gray-900'
                        }`}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>
                <div className="flex items-center gap-3">
                  <div className="relative">
                    <Search size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search processes"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <button className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50">
                    <Filter size={16} />
                    Filters
                  </button>
                  <button
                    onClick={() => fetchAnalytics()}
                    className="p-2 text-gray-400 hover:text-gray-600"
                    disabled={loading}
                  >
                    <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
                  </button>
                </div>
              </div>

              {/* Table */}
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-6 py-3 text-left">
                        <input
                          type="checkbox"
                          className="rounded border-gray-300"
                          onChange={(e) => {
                            setProcesses(processes.map(process => ({
                              ...process,
                              selected: e.target.checked
                            })));
                          }}
                        />
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Process Name
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Instances
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Automation %
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Manual Tasks
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Bot Hours
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Cost Savings
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        ROI %
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Category
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {loading ? (
                      <>
                        {[1, 2, 3, 4, 5].map((i) => (
                          <tr key={i} className="animate-pulse">
                            <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-4"></div></td>
                            <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-32"></div></td>
                            <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-16"></div></td>
                            <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-12"></div></td>
                            <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-16"></div></td>
                            <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-12"></div></td>
                            <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-20"></div></td>
                            <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-12"></div></td>
                            <td className="px-6 py-4"><div className="h-5 bg-gray-200 rounded-full w-16"></div></td>
                            <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-12"></div></td>
                          </tr>
                        ))}
                      </>
                    ) : paginatedProcesses.length === 0 ? (
                      <tr>
                        <td colSpan={10} className="px-6 py-8 text-center text-gray-500">
                          No process data available
                        </td>
                      </tr>
                    ) : (
                      paginatedProcesses.map((process) => (
                        <tr key={process.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <input
                              type="checkbox"
                              checked={process.selected || false}
                              onChange={() => toggleProcessSelection(process.id)}
                              className="rounded border-gray-300"
                            />
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {process.name}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {formatNumber(process.instances)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {process.automationRate}%
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {formatNumber(process.manualTasks)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {formatNumber(process.botHours)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {formatCurrency(process.costSavings)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {process.roi}%
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getCategoryColor(process.category)}`}>
                              {process.category}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            <div className="flex items-center gap-2">
                              <button className="text-gray-400 hover:text-gray-600">
                                <Edit size={16} />
                              </button>
                              <button className="text-gray-400 hover:text-red-600">
                                <Trash2 size={16} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100">
                <div className="text-sm text-gray-500">
                  Showing {currentPage * pageSize + 1} to {Math.min((currentPage + 1) * pageSize, processes.length)} of {processes.length}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
                    disabled={currentPage === 0}
                    className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => (
                    <button
                      key={i}
                      onClick={() => setCurrentPage(i)}
                      className={`px-3 py-1.5 text-sm rounded-lg ${currentPage === i
                          ? 'bg-purple-600 text-white'
                          : 'border border-gray-200 text-gray-600 hover:bg-gray-50'
                        }`}
                    >
                      {i + 1}
                    </button>
                  ))}
                  <button
                    onClick={() => setCurrentPage(Math.min(totalPages - 1, currentPage + 1))}
                    disabled={currentPage >= totalPages - 1}
                    className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsPage;
